import React, { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useEvents } from "@/hooks/useEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

interface EventFormData {
  title: string;
  description: string;
  date: string;
  start_time: string;
  end_time: string;
  image_url?: string;
  is_featured: boolean;
}

const AdminEvents: React.FC = () => {
  const { data: events = [] } = useEvents();
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState<number | null>(null);
  const [eventForm, setEventForm] = useState<EventFormData>({
    title: "",
    description: "",
    date: "",
    start_time: "",
    end_time: "",
    image_url: "",
    is_featured: false
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleAddEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("events")
        .insert([eventForm]);

      if (error) throw error;

      toast({ title: "Success", description: "Event added successfully" });
      setIsAddingEvent(false);
      setEventForm({
        title: "",
        description: "",
        date: "",
        start_time: "",
        end_time: "",
        image_url: "",
        is_featured: false
      });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add event",
      });
    }
  };

  const handleUpdateEvent = async (id: number) => {
    try {
      const { error } = await supabase
        .from("events")
        .update(eventForm)
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Success", description: "Event updated successfully" });
      setEditingEvent(null);
      setEventForm({
        title: "",
        description: "",
        date: "",
        start_time: "",
        end_time: "",
        image_url: "",
        is_featured: false
      });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update event",
      });
    }
  };

  const handleDeleteEvent = async (id: number) => {
    if (!confirm("Are you sure you want to delete this event?")) return;

    try {
      const { error } = await supabase
        .from("events")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Success", description: "Event deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["events"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete event",
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-lounge-gold">Events Management</h1>
          <p className="text-lounge-light/60 mt-2">Manage your upcoming events</p>
        </div>

        <div className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-display font-semibold text-lounge-gold">Events</h2>
            <Button
              onClick={() => setIsAddingEvent(true)}
              className="bg-lounge-gold hover:bg-lounge-gold/90"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Event
            </Button>
          </div>

          {isAddingEvent && (
            <form onSubmit={handleAddEvent} className="mb-6 space-y-4 bg-lounge-dark p-4 rounded-lg">
              <Input
                placeholder="Event Title"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                required
              />
              <Textarea
                placeholder="Event Description"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                required
              />
              <div className="grid grid-cols-3 gap-4">
                <Input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  className="bg-lounge-charcoal border-lounge-gold/20"
                  required
                />
                <Input
                  type="time"
                  value={eventForm.start_time}
                  onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                  className="bg-lounge-charcoal border-lounge-gold/20"
                  required
                />
                <Input
                  type="time"
                  value={eventForm.end_time}
                  onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                  className="bg-lounge-charcoal border-lounge-gold/20"
                  required
                />
              </div>
              <Input
                placeholder="Image URL (optional)"
                value={eventForm.image_url}
                onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
              />
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={eventForm.is_featured}
                  onChange={(e) => setEventForm({ ...eventForm, is_featured: e.target.checked })}
                  className="bg-lounge-charcoal border-lounge-gold/20"
                />
                <label htmlFor="is_featured" className="text-lounge-light">Featured Event</label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-lounge-gold hover:bg-lounge-gold/90">
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsAddingEvent(false);
                    setEventForm({
                      title: "",
                      description: "",
                      date: "",
                      start_time: "",
                      end_time: "",
                      image_url: "",
                      is_featured: false
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {events.map((event) => (
              <div
                key={event.id}
                className="flex items-center justify-between p-4 bg-lounge-dark rounded-lg"
              >
                {editingEvent === event.id ? (
                  <div className="flex-1 space-y-4">
                    <Input
                      value={eventForm.title}
                      onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                      className="bg-lounge-charcoal border-lounge-gold/20"
                    />
                    <Textarea
                      value={eventForm.description}
                      onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                      className="bg-lounge-charcoal border-lounge-gold/20"
                    />
                    <div className="grid grid-cols-3 gap-4">
                      <Input
                        type="date"
                        value={eventForm.date}
                        onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                        className="bg-lounge-charcoal border-lounge-gold/20"
                      />
                      <Input
                        type="time"
                        value={eventForm.start_time}
                        onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                        className="bg-lounge-charcoal border-lounge-gold/20"
                      />
                      <Input
                        type="time"
                        value={eventForm.end_time}
                        onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                        className="bg-lounge-charcoal border-lounge-gold/20"
                      />
                    </div>
                    <Input
                      placeholder="Image URL (optional)"
                      value={eventForm.image_url}
                      onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })}
                      className="bg-lounge-charcoal border-lounge-gold/20"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`is_featured_${event.id}`}
                        checked={eventForm.is_featured}
                        onChange={(e) => setEventForm({ ...eventForm, is_featured: e.target.checked })}
                        className="bg-lounge-charcoal border-lounge-gold/20"
                      />
                      <label htmlFor={`is_featured_${event.id}`} className="text-lounge-light">Featured Event</label>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleUpdateEvent(event.id)}
                        className="bg-lounge-gold hover:bg-lounge-gold/90"
                      >
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingEvent(null);
                          setEventForm({
                            title: "",
                            description: "",
                            date: "",
                            start_time: "",
                            end_time: "",
                            image_url: "",
                            is_featured: false
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 className="font-medium text-lounge-light">{event.title}</h3>
                      <p className="text-sm text-lounge-light/60">{event.description}</p>
                      <p className="text-sm text-lounge-gold mt-1">
                        {format(new Date(event.date), "MMMM d, yyyy")} | {event.start_time} - {event.end_time}
                      </p>
                      {event.is_featured && (
                        <span className="inline-block bg-lounge-gold/20 text-lounge-gold text-xs px-2 py-1 rounded-full mt-1">
                          Featured
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setEditingEvent(event.id);
                          setEventForm({
                            title: event.title,
                            description: event.description,
                            date: event.date,
                            start_time: event.start_time,
                            end_time: event.end_time,
                            image_url: event.image_url || "",
                            is_featured: event.is_featured
                          });
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteEvent(event.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminEvents; 